import { useEffect, useMemo, useState } from "react";

export function FilterSidebar({ products, filters, onChange }) {
    const { fabrics, colors, occasions, sizes, subTypes, priceRange } = useMemo(() => {
        const fabricSet = new Set();
        const colorSet = new Set();
        const occasionSet = new Set();
        const sizeSet = new Set();
        const subTypeSet = new Set();
        let min = Infinity;
        let max = -Infinity;
        for (const p of products) {
            if (p.fabric) fabricSet.add(p.fabric);
            if (p.color) colorSet.add(p.color);
            if (p.subType) subTypeSet.add(p.subType);
            (p.occasion || []).forEach((o) => occasionSet.add(o));
            (p.sizes || []).forEach((s) => sizeSet.add(s));
            if (p.price < min) min = p.price;
            if (p.price > max) max = p.price;
        }
        return {
            fabrics: [...fabricSet].sort(),
            colors: [...colorSet].sort(),
            occasions: [...occasionSet].sort(),
            sizes: [...sizeSet].sort(),
            subTypes: [...subTypeSet].sort(),
            priceRange: { min: Number.isFinite(min) ? min : 0, max: Number.isFinite(max) ? max : 0 },
        };
    }, [products]);

    // Local state for price inputs — commit explicitly so the URL/API call is deterministic.
    const [minStr, setMinStr] = useState(filters.minPrice != null ? String(filters.minPrice) : "");
    const [maxStr, setMaxStr] = useState(filters.maxPrice != null ? String(filters.maxPrice) : "");

    useEffect(() => {
        setMinStr(filters.minPrice != null ? String(filters.minPrice) : "");
        setMaxStr(filters.maxPrice != null ? String(filters.maxPrice) : "");
    }, [filters.minPrice, filters.maxPrice]);

    const set = (key, value) => {
        const next = { ...filters };
        if (value === undefined || value === null || value === "") {
            delete next[key];
        } else {
            next[key] = value;
        }
        onChange(next);
    };

    const applyPrice = () => {
        const next = { ...filters };
        const minN = minStr.trim() === "" ? null : Number(minStr);
        const maxN = maxStr.trim() === "" ? null : Number(maxStr);
        if (minN != null && Number.isFinite(minN)) next.minPrice = minN;
        else delete next.minPrice;
        if (maxN != null && Number.isFinite(maxN)) next.maxPrice = maxN;
        else delete next.maxPrice;
        onChange(next);
    };

    const Group = ({ title, children, testId }) => (
        <div className="py-5 border-b border-inkDark/5" data-testid={testId}>
            <div className="label-eyebrow mb-3">{title}</div>
            {children}
        </div>
    );

    const Pill = ({ active, onClick, children, testId }) => (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                active
                    ? "bg-skyblue text-inkDark border-skyblue"
                    : "bg-ivory text-inkDark border-inkDark/10 hover:border-skyblueDeep"
            }`}
        >
            {children}
        </button>
    );

    return (
        <aside className="bg-ivory rounded-2xl p-6 border border-inkDark/5 sticky top-28" data-testid="filter-sidebar">
            <div className="flex items-center justify-between">
                <div className="font-display text-xl text-inkDark">Filter</div>
                <button
                    onClick={() => onChange({})}
                    className="text-sm text-skyblueDeep hover:underline"
                    data-testid="clear-filters-button"
                >
                    Clear
                </button>
            </div>

            {subTypes.length ? (
                <Group title="Type" testId="filter-group-subtype">
                    <div className="flex flex-wrap gap-2">
                        {subTypes.map((t) => (
                            <Pill
                                key={t}
                                active={filters.subType === t}
                                onClick={() => set("subType", filters.subType === t ? "" : t)}
                                testId={`filter-subtype-${t.toLowerCase()}`}
                            >
                                {t}
                            </Pill>
                        ))}
                    </div>
                </Group>
            ) : null}

            <Group title="Price (INR)" testId="filter-group-price">
                <div className="grid grid-cols-2 gap-3">
                    <input
                        type="number"
                        value={minStr}
                        onChange={(e) => setMinStr(e.target.value)}
                        onBlur={applyPrice}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPrice(); } }}
                        placeholder={`Min ${priceRange.min}`}
                        className="rounded-lg border border-inkDark/10 px-3 py-2 text-sm bg-white"
                        data-testid="filter-min-price"
                    />
                    <input
                        type="number"
                        value={maxStr}
                        onChange={(e) => setMaxStr(e.target.value)}
                        onBlur={applyPrice}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPrice(); } }}
                        placeholder={`Max ${priceRange.max}`}
                        className="rounded-lg border border-inkDark/10 px-3 py-2 text-sm bg-white"
                        data-testid="filter-max-price"
                    />
                </div>
                <button
                    type="button"
                    onClick={applyPrice}
                    className="mt-3 w-full rounded-full px-4 py-2 text-sm bg-skyblue text-inkDark hover:bg-skyblueDeep hover:text-white transition-colors"
                    data-testid="filter-apply-price-button"
                >
                    Apply price
                </button>
            </Group>

            {fabrics.length ? (
                <Group title="Fabric" testId="filter-group-fabric">
                    <div className="flex flex-wrap gap-2">
                        {fabrics.map((f) => (
                            <Pill
                                key={f}
                                active={filters.fabric === f}
                                onClick={() => set("fabric", filters.fabric === f ? "" : f)}
                                testId={`filter-fabric-${f.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                                {f}
                            </Pill>
                        ))}
                    </div>
                </Group>
            ) : null}

            {colors.length ? (
                <Group title="Color" testId="filter-group-color">
                    <div className="flex flex-wrap gap-2">
                        {colors.map((c) => (
                            <Pill
                                key={c}
                                active={filters.color === c}
                                onClick={() => set("color", filters.color === c ? "" : c)}
                                testId={`filter-color-${c.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                                {c}
                            </Pill>
                        ))}
                    </div>
                </Group>
            ) : null}

            {sizes.length ? (
                <Group title="Size" testId="filter-group-size">
                    <div className="flex flex-wrap gap-2">
                        {sizes.map((s) => (
                            <Pill
                                key={s}
                                active={filters.size === s}
                                onClick={() => set("size", filters.size === s ? "" : s)}
                                testId={`filter-size-${s}`}
                            >
                                {s}
                            </Pill>
                        ))}
                    </div>
                </Group>
            ) : null}

            {occasions.length ? (
                <Group title="Occasion" testId="filter-group-occasion">
                    <div className="flex flex-wrap gap-2">
                        {occasions.map((o) => (
                            <Pill
                                key={o}
                                active={filters.occasion === o}
                                onClick={() => set("occasion", filters.occasion === o ? "" : o)}
                                testId={`filter-occasion-${o.toLowerCase()}`}
                            >
                                {o}
                            </Pill>
                        ))}
                    </div>
                </Group>
            ) : null}
        </aside>
    );
}
